/**
 * PlazaSalesPage — proper long-scroll sales page for hustleplaza.au.
 *
 * Design brief:
 *   • Give a first-time visitor a *reason to sign up* in the first 5 seconds
 *   • Communicate the flywheel (spend here → earn HustlePoints → spend
 *     anywhere in HustleSuite) as the clear differentiator
 *   • Show real Aussie product imagery, real vendor snippets, real reviews
 *   • Every user-facing string flows through ``t(...)`` so the page
 *     translates the moment the LanguagePicker switches
 *
 * Layout (each is a self-contained subcomponent for readability):
 *    Nav → Hero → StatsStrip → ProblemBand → SolutionGrid
 *   → RewardsFlywheel → CategoryGrid → FeaturedVendors → HowItWorks
 *   → CompareTable → TrustBand → Testimonials → FAQ → FinalCta → Footer
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ShoppingCart, ShieldCheck, ArrowRight, Sparkles, Star,
  Truck, CheckCircle2, XCircle, Coffee, Utensils, Scissors,
  Bed, Wrench, HeartHandshake, ChevronDown, Zap,
  Mic, Edit3, Bookmark, Search, MessageSquare, LayoutDashboard, Rocket,
} from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { MASTER_GROUPS } from "./plazaProducts";

const APP_URL = "https://app.hustleplaza.au";
// Logo naming: "LIGHT" file = light-theme version (dark ink on light bg),
// "DARK" file = dark-theme version (light ink on dark bg). This
// salespage is dark, so we use PLAZA DARK.
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/ecrffi9b_Local%20Hustle%20Logo%20-%20PLAZA%20DARK.png";

// Generated via Gemini Nano Banana → /app/scripts/gen_plaza_images.py.
// Files live in /app/frontend/public/gen/plaza/ so they ship in the
// build under /gen/plaza/*.png (long TTL via nginx).
const GEN = "/gen/plaza";
const HERO_IMAGES = [
  `${GEN}/hero-1-headphones.png`,
  `${GEN}/hero-2-honey.png`,
  `${GEN}/hero-3-coffee-beans.png`,
  `${GEN}/hero-4-linen-shirt.png`,
  `${GEN}/hero-5-skincare.png`,
  `${GEN}/hero-6-pet-treat.png`,
];
const CATEGORY_IMAGES = {
  "groceries-pantry":     `${GEN}/cat-groceries.png`,
  "groceries-beverages":  `${GEN}/cat-beverages.png`,
  "whitegoods":           `${GEN}/cat-appliances.png`,
  "audio-video":          `${GEN}/cat-audio.png`,
  "baby-kids":            `${GEN}/cat-fashion.png`,   // baby/kidswear
  "furniture":            `${GEN}/cat-homewares.png`,
  "auto-accessories":     `${GEN}/cat-outdoor.png`,
  "electronics":          `${GEN}/cat-tools.png`,
};
// VENDOR_IMAGES removed with the FeaturedVendors section — the six
// generated vendor JPGs (vendor-*.png) are still on disk under
// /gen/plaza/ in case we bring the section back with real vendors.

export default function PlazaSalesPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const lang = (i18n.language || "en").split("-")[0];
    document.documentElement.setAttribute("lang", lang);
    document.title = `HustlePlaza — ${t("plaza_hero_title_1")} ${t("plaza_hero_title_2")}`;
  }, [i18n.language, t]);

  return (
    <div
      data-testid="plaza-sales-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 antialiased transition-colors"
    >
      <PlatformHeader salesTopGap
        brand={{ name: "HustlePlaza", to: "/" }}
        nav={[
          { to: "/",                            label: t("plaza_nav_home",     { defaultValue: "Home" }) },
          { to: "#categories",                  label: t("plaza_nav_categories",{ defaultValue: "Categories" }) },
          { to: "#how-it-works",                label: t("plaza_nav_how",       { defaultValue: "How it works" }) },
          { to: "https://app.hustleplaza.au/register", label: t("plaza_nav_join", { defaultValue: "Join free" }), align: "right", variant: "primary", external: true },
          { to: "https://app.hustleplaza.au/login",    label: t("plaza_nav_signin",{ defaultValue: "Sign in" }),   align: "right", external: true },
        ]}
        showBookCta={false}
        showCart={false}
      />
      <Hero t={t} />
      <ProblemBand t={t} />
      <SolutionGrid t={t} />
      <RewardsFlywheel t={t} />
      <CategoryGrid t={t} />
      <HowItWorks t={t} />
      <CompareTable t={t} />
      <TrustBand t={t} />
      <RoadmapSection t={t} />
      <Testimonials t={t} />
      <FAQSection t={t} />
      <FinalCta t={t} />
      <SuiteFooter />
    </div>
  );
}

/* ═════════════════════════ 1. NAV ═════════════════════════ */
function TopNav({ t }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <nav
      data-testid="plaza-sales-nav"
      className={`fixed top-[72px] left-0 right-0 z-50 transition-all
        ${scrolled ? "bg-[#0a0908]/85 backdrop-blur-md border-b border-stone-800"
                    : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_DARK} alt="HustlePlaza" className="h-8 w-auto" />
          <span className="hidden sm:block text-[10px] text-stone-500">
            part of{" "}
            <a href="https://hustlesuite.au" className="text-amber-400 font-semibold">
              HustleSuite
            </a>
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm text-stone-400">
          <a href="#solution"  className="hover:text-amber-400">{t("plaza_nav_features")}</a>
          <a href="#rewards"   className="hover:text-amber-400">{t("plaza_nav_rewards")}</a>
          <a href="#how"       className="hover:text-amber-400">{t("plaza_nav_how")}</a>
          <a href="#roadmap"   className="hover:text-amber-400">{t("plaza_nav_roadmap")}</a>
          <a href="#faq"       className="hover:text-amber-400">{t("plaza_nav_faq")}</a>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={`${APP_URL}/login`}
            data-testid="plaza-sales-signin"
            className="hidden sm:inline-flex items-center h-9 px-4 rounded-full text-sm text-stone-100 hover:text-amber-400"
          >
            {t("plaza_cta_signin")}
          </a>
          <a
            href={`${APP_URL}/register`}
            data-testid="plaza-sales-join"
            className="inline-flex items-center h-9 px-4 md:px-5 rounded-full text-sm font-bold text-stone-950 bg-amber-400 hover:bg-amber-300 transition"
          >
            {t("plaza_cta_start")}
          </a>
        </div>
      </div>
    </nav>
  );
}

/* ═════════════════════════ 2. HERO ═════════════════════════ */
function Hero({ t }) {
  return (
    <header
      data-testid="plaza-sales-hero"
      className="relative pt-32 md:pt-40 pb-16 md:pb-28 px-6 overflow-hidden
                  bg-[radial-gradient(1200px_600px_at_80%_-20%,#8b6d2f22,transparent),radial-gradient(800px_400px_at_20%_120%,#00654012,transparent)]"
    >
      <div className="absolute inset-0 -z-10 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(rgba(251,191,36,.35) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
      />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {/* Left: copy */}
        <div>
          <span
            data-testid="plaza-sales-hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1 text-[11px] font-bold text-amber-300 uppercase tracking-[0.2em]"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"/>
            {t("plaza_hero_badge")}
          </span>
          <p className="mt-6 text-amber-400 text-sm font-bold uppercase tracking-[0.28em]">
            {t("plaza_hero_kicker")}
          </p>
          <h1
            data-testid="plaza-sales-hero-title"
            className="mt-3 text-5xl md:text-7xl lg:text-[5.5rem] leading-[1] font-black tracking-tight"
          >
            {t("plaza_hero_title_1")}<br/>
            <span className="text-amber-400">{t("plaza_hero_title_2")}</span>
          </h1>
          <p className="mt-6 text-lg text-stone-700 dark:text-stone-300 leading-relaxed max-w-xl">
            {t("plaza_hero_sub")}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`${APP_URL}/register`}
              data-testid="plaza-sales-hero-cta-primary"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-black text-stone-950 bg-amber-400 hover:bg-amber-300 shadow-[0_0_60px_-15px_#fbbf2488] transition"
            >
              {t("plaza_cta_start")} <ArrowRight size={16} />
            </a>
            <a
              href={`${APP_URL}/plaza/shop`}
              data-testid="plaza-sales-hero-cta-shop"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full text-sm font-semibold text-stone-900 dark:text-stone-100 border border-stone-400 dark:border-stone-700 hover:border-amber-500 hover:text-amber-500 dark:hover:text-amber-400 transition"
            >
              <ShoppingCart size={16} /> {t("plaza_cta_shop")}
            </a>
          </div>
          <div className="mt-6 flex items-center gap-2 text-[11px] text-stone-600 dark:text-stone-500">
            <ShieldCheck size={12} className="text-emerald-500 dark:text-emerald-400" />
            <span>Free forever · No card required · Cancel anytime</span>
          </div>
        </div>
        {/* Right: product collage */}
        <div className="relative hidden lg:block">
          <div className="grid grid-cols-3 gap-3 rotate-[8deg] scale-105">
            {HERO_IMAGES.map((src, i) => (
              <img
                src={src}
                alt=""
                loading="lazy"
                className={`aspect-square object-cover rounded-2xl border border-stone-300 dark:border-stone-800
                            ${i === 1 ? "translate-y-6" : ""}
                            ${i === 3 ? "translate-y-6" : ""}
                            ${i === 5 ? "translate-y-3" : ""}`}
              />
            ))}
          </div>
          {/* floating rewards chip — illustrative example of what points look like */}
          <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 rotate-[-4deg]
                             bg-emerald-400 text-emerald-950 px-4 py-2 rounded-2xl shadow-2xl">
            <div className="font-black text-lg leading-none">+HustlePoints</div>
            <div className="text-[11px] font-semibold opacity-80 mt-0.5">
              on every order you place
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

/* StatsStrip removed — the fabricated numbers (12,847 products, 340+ vendors,
   $4.2M paid) could not be substantiated at launch. Re-enable once the
   real metrics are available from the database. */

/* ═════════════════════════ 4. PROBLEM BAND ═════════════════════════ */
function ProblemBand({ t }) {
  const items = ["plaza_problem_1", "plaza_problem_2", "plaza_problem_3", "plaza_problem_4"];
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
      <div className="max-w-2xl">
        <p className="text-rose-400 text-xs font-bold uppercase tracking-[0.28em]">
          {t("plaza_problem_kicker")}
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
          {t("plaza_problem_title")}
        </h2>
        <p className="mt-5 text-stone-700 dark:text-stone-400 leading-relaxed">
          {t("plaza_problem_sub")}
        </p>
      </div>
      <ul className="mt-10 grid sm:grid-cols-2 gap-3">
        {items.map((k) => (
          <li
            key={k}
            className="flex items-start gap-3 rounded-2xl border border-stone-800 bg-stone-950/40 p-4"
          >
            <XCircle size={18} className="text-rose-400 shrink-0 mt-0.5"/>
            <span className="text-sm text-stone-300">{t(k)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ═════════════════════════ 5. SOLUTION GRID ═════════════════════════ */
function SolutionGrid({ t }) {
  const items = [
    ["plaza_solution_1_t", "plaza_solution_1_b", ShoppingCart, "#fbbf24"],
    ["plaza_solution_2_t", "plaza_solution_2_b", ShieldCheck,  "#34d399"],
    ["plaza_solution_3_t", "plaza_solution_3_b", Sparkles,     "#f472b6"],
    ["plaza_solution_4_t", "plaza_solution_4_b", Truck,        "#60a5fa"],
  ];
  return (
    <section id="solution"
      className="border-t border-stone-900 bg-gradient-to-b from-stone-950 to-[#0a0908] px-6 py-20 md:py-28"
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_solution_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_solution_title")}
          </h2>
          <p className="mt-5 text-stone-400 leading-relaxed">
            {t("plaza_solution_sub")}
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
              data-testid="plaza-sales-solution-grid">
          {items.map(([titleKey, bodyKey, Icon, color], i) => (
            <div
              key={titleKey}
              data-testid={`plaza-sales-solution-${i + 1}`}
              className="rounded-2xl border border-stone-800 bg-stone-950/40 p-6 hover:border-amber-400/60 hover:-translate-y-1 transition"
            >
              <div className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${color}22` }}>
                <Icon size={22} style={{ color }} />
              </div>
              <h3 className="mt-4 font-bold text-lg">{t(titleKey)}</h3>
              <p className="mt-2 text-sm text-stone-400 leading-relaxed">
                {t(bodyKey)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ 6. REWARDS FLYWHEEL ═════════════════════════ */
function RewardsFlywheel({ t }) {
  const steps = [
    ["plaza_rewards_step_1", "plaza_rewards_step_1_b", ShoppingCart],
    ["plaza_rewards_step_2", "plaza_rewards_step_2_b", Zap],
    ["plaza_rewards_step_3", "plaza_rewards_step_3_b", HeartHandshake],
    ["plaza_rewards_step_4", "plaza_rewards_step_4_b", Sparkles],
  ];
  const businesses = [
    [Utensils, "Restaurants"],
    [Coffee,   "Cafés"],
    [Bed,      "Hotels"],
    [Scissors, "Hairdressers"],
    [Wrench,   "Tradies"],
    [HeartHandshake, "Pet groomers"],
  ];
  return (
    <section id="rewards"
      className="relative bg-[#0a0908] px-6 py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute -top-32 right-0 w-[600px] h-[600px] rounded-full bg-amber-400/5 blur-3xl"/>
      <div className="max-w-6xl mx-auto relative">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_rewards_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_rewards_title")}
          </h2>
          <p className="mt-5 text-stone-400 leading-relaxed">
            {t("plaza_rewards_sub")}
          </p>
        </div>

        {/* 4-step flywheel row */}
        <ol className="mt-14 grid md:grid-cols-4 gap-4"
             data-testid="plaza-sales-rewards-steps">
          {steps.map(([titleKey, bodyKey, Icon], i) => (
            <li key={titleKey} className="relative">
              <div className="rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-400/10 to-transparent p-6 h-full">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-stone-950 font-black text-sm">
                    {i + 1}
                  </span>
                  <Icon size={18} className="text-amber-400"/>
                </div>
                <h3 className="mt-4 font-bold">{t(titleKey)}</h3>
                <p className="mt-1 text-xs text-stone-400 leading-relaxed">
                  {t(bodyKey)}
                </p>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight size={20}
                  className="hidden md:block absolute top-1/2 -right-4 -translate-y-1/2 text-amber-400/60"/>
              )}
            </li>
          ))}
        </ol>

        {/* Where you can spend */}
        <div className="mt-14 rounded-3xl border border-stone-800 bg-gradient-to-br from-emerald-950/40 to-stone-950/40 p-8 md:p-10">
          <h3 className="text-xl font-black text-emerald-300">
            Where you can spend your HustlePoints
          </h3>
          <p className="text-sm text-stone-400 mt-2 max-w-xl">
            Every business that runs on HustleSuite accepts HustlePoints.
            Here&apos;s the sort of place you&apos;ll be able to spend
            them:
          </p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {businesses.map(([Icon, name]) => (
              <div key={name}
                    className="rounded-2xl border border-stone-800 bg-stone-950/60 px-3 py-4 text-center hover:border-emerald-400/60 transition">
                <Icon size={22} className="mx-auto text-emerald-400"/>
                <div className="mt-2 text-xs font-semibold text-stone-200">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ 7. CATEGORY GRID ═════════════════════════ */
function CategoryGrid({ t }) {
  // Show first 8 master groups with photos from picsum via master_group tag.
  const cats = useMemo(() => MASTER_GROUPS.slice(0, 8), []);
  return (
    <section className="max-w-7xl mx-auto px-6 py-20 md:py-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-xl">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_cats_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_cats_title")}
          </h2>
          <p className="mt-4 text-stone-700 dark:text-stone-400 leading-relaxed">
            {t("plaza_cats_sub")}
          </p>
        </div>
        <a
          href={`${APP_URL}/plaza/shop`}
          className="text-sm text-amber-400 hover:text-amber-300 font-semibold inline-flex items-center gap-1"
        >
          {t("plaza_cats_cta")} <ArrowRight size={14}/>
        </a>
      </div>
      <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-4 gap-4"
            data-testid="plaza-sales-cats-grid">
        {cats.map((c, i) => (
          <a
            key={c.tag}
            href={`${APP_URL}/plaza/shop?g=${encodeURIComponent(c.tag)}`}
            data-testid={`plaza-sales-cat-${c.tag}`}
            className={`group relative overflow-hidden rounded-2xl border border-stone-800 aspect-[4/5] block
                         ${i === 0 || i === 3 ? "sm:col-span-1 md:aspect-[4/5]" : ""}`}
          >
            <img
              src={CATEGORY_IMAGES[c.tag] || `${GEN}/cat-homewares.png`}
              alt={c.name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                {c.subs?.[0] || "Live now"}
              </div>
              <div className="mt-1 font-black text-lg leading-tight text-stone-50">
                {c.name}
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

/* FeaturedVendors removed — the illustrative businesses (Nectar & Comb,
   The Roast Room, Blue Gum Tools, Little Wren Kids, Cape York Naturals,
   Yarra Pet Co) would fail ABN lookup and any "ABN verified" claim
   we can't back up gives competitors an easy shot at us. Bring this
   section back only when real onboarded vendors exist. */

/* ═════════════════════════ 9. HOW IT WORKS ═════════════════════════ */
function HowItWorks({ t }) {
  const steps = [
    ["plaza_how_1_title", "plaza_how_1_body"],
    ["plaza_how_2_title", "plaza_how_2_body"],
    ["plaza_how_3_title", "plaza_how_3_body"],
  ];
  return (
    <section id="how" className="max-w-6xl mx-auto px-6 py-20 md:py-28">
      <div className="max-w-2xl">
        <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
          {t("plaza_how_kicker")}
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
          {t("plaza_how_title")}
        </h2>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-4">
        {steps.map(([titleKey, bodyKey], i) => (
          <div key={titleKey}
                className="rounded-2xl border border-stone-800 bg-stone-950/40 p-6 relative">
            <span className="absolute -top-3 -left-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-400 text-stone-950 font-black">
              {i + 1}
            </span>
            <h3 className="mt-3 font-bold text-lg">{t(titleKey)}</h3>
            <p className="mt-2 text-sm text-stone-400 leading-relaxed">
              {t(bodyKey)}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═════════════════════════ 10. COMPARE TABLE ═════════════════════════ */
function CompareCell({ v }) {
  if (v === true)  return <CheckCircle2 size={20} className="mx-auto text-emerald-400"/>;
  if (v === false) return <XCircle      size={20} className="mx-auto text-stone-600 dark:text-stone-400"/>;
  return <span className="text-stone-400 text-xs">{v}</span>;
}

function CompareTable({ t }) {
  const cols = [
    { key: "us",    label: "plaza_compare_col_us",    us: true },
    { key: "amz",   label: "plaza_compare_col_amz" },
    { key: "ebay",  label: "plaza_compare_col_ebay" },
    { key: "kmart", label: "plaza_compare_col_kmart" },
  ];
  const rows = [
    ["plaza_compare_row_1", true, false, false, true],
    ["plaza_compare_row_2", true, false, false, false],
    ["plaza_compare_row_3", true, false, false, false],
    ["plaza_compare_row_4", true, false, false, false],
    ["plaza_compare_row_5", true, false, false, true],
  ];
  return (
    <section className="border-t border-stone-900 bg-stone-950/60 px-6 py-20 md:py-24">
      <div className="max-w-5xl mx-auto">
        <div className="max-w-2xl">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_compare_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_compare_title")}
          </h2>
        </div>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-stone-800"
              data-testid="plaza-sales-compare">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-stone-950/60 border-b border-stone-800">
              <tr>
                <th className="text-left p-4 text-stone-400 font-semibold w-2/5"></th>
                {cols.map((c) => (
                  <th key={c.key}
                       className={`p-4 font-black text-center ${c.us ? "text-amber-400" : "text-stone-400"}`}>
                    {t(c.label)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-800">
              {rows.map(([labelKey, ...vals]) => (
                <tr key={labelKey}>
                  <td className="p-4 text-stone-200">{t(labelKey)}</td>
                  {vals.map((v, i) => (
                    <td key={i}
                         className={`p-4 text-center ${cols[i].us ? "bg-amber-400/5" : ""}`}>
                      <CompareCell v={v}/>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ 11. TRUST BAND ═════════════════════════ */
function TrustBand({ t }) {
  const items = ["plaza_trust_1", "plaza_trust_2", "plaza_trust_3", "plaza_trust_4"];
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 md:py-24">
      <div className="rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-emerald-950/40 to-stone-950/40 p-8 md:p-12 grid md:grid-cols-2 gap-8 items-start">
        <div>
          <ShieldCheck size={48} className="text-emerald-400"/>
          <p className="mt-6 text-emerald-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_trust_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-black leading-tight">
            {t("plaza_trust_title")}
          </h2>
          <p className="mt-4 text-stone-400 leading-relaxed">
            {t("plaza_trust_body")}
          </p>
        </div>
        <ul className="space-y-3">
          {items.map((k) => (
            <li key={k}
                 className="flex items-start gap-3 rounded-xl bg-stone-950/50 border border-stone-800 p-4">
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5"/>
              <span className="text-sm text-stone-200">{t(k)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ═════════════════════════ 12. TESTIMONIALS ═════════════════════════ */

/**
 * RoadmapCard — HustlePlaza dark-theme roadmap tile.
 *
 * iter268c: the "next" tone previously read `bg-emerald-400/15
 * text-emerald-300` — a translucent pastel with light text that
 * washed out to unreadable on the dark-stone card (and would fail
 * WCAG AA on any lighter card too). Bump both variants to solid
 * high-contrast pills.
 */
function RoadmapCard({ t, titleKey, bodyKey, Icon, badge, badgeTone }) {
  return (
    <div className="rounded-2xl border border-stone-800 bg-stone-950/40 p-6 hover:border-amber-400/60 transition">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-400/10 flex items-center justify-center">
          <Icon size={18} className="text-amber-400" />
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border
                          ${badgeTone === "next"
                            ? "bg-amber-100 text-amber-900 border-amber-400"
                            : "bg-emerald-100 text-emerald-900 border-emerald-400"}`}>
          {badge}
        </span>
      </div>
      <h3 className="mt-4 font-bold text-base leading-tight">{t(titleKey)}</h3>
      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{t(bodyKey)}</p>
    </div>
  );
}

/* Roadmap of upcoming HustleSuite Chrome-extension features. Turns
   work-in-progress into a marketing asset — "you're joining a platform
   that's still adding tools" is a powerful founder-signup signal. */
function RoadmapSection({ t }) {
  const soon = [
    ["plaza_roadmap_1_t", "plaza_roadmap_1_b", Mic],
    ["plaza_roadmap_2_t", "plaza_roadmap_2_b", Edit3],
    ["plaza_roadmap_3_t", "plaza_roadmap_3_b", Bookmark],
  ];
  const next = [
    ["plaza_roadmap_4_t", "plaza_roadmap_4_b", Search],
    ["plaza_roadmap_5_t", "plaza_roadmap_5_b", MessageSquare],
    ["plaza_roadmap_6_t", "plaza_roadmap_6_b", LayoutDashboard],
  ];
  return (
    <section
      id="roadmap"
      data-testid="plaza-sales-roadmap"
      className="border-t border-stone-900 bg-gradient-to-b from-[#0a0908] to-stone-950/60 px-6 py-20 md:py-24"
    >
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em] flex items-center gap-2">
            <Rocket size={14}/> {t("plaza_roadmap_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_roadmap_title")}
          </h2>
          <p className="mt-4 text-stone-400 leading-relaxed">
            {t("plaza_roadmap_sub")}
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-4"
              data-testid="plaza-sales-roadmap-soon">
          {soon.map(([tk, bk, Icon]) => (
            <RoadmapCard key={tk} t={t} titleKey={tk} bodyKey={bk} Icon={Icon}
                   badge={t("plaza_roadmap_soon")} badgeTone="soon" />
          ))}
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-4"
              data-testid="plaza-sales-roadmap-next">
          {next.map(([tk, bk, Icon]) => (
            <RoadmapCard key={tk} t={t} titleKey={tk} bodyKey={bk} Icon={Icon}
                   badge={t("plaza_roadmap_next")} badgeTone="next" />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ 13. TESTIMONIALS ═════════════════════════ */
function Testimonials({ t }) {
  // We keep first-name attribution (that's normal for reviews) but drop
  // suburb / state / business affiliation because we can't back those
  // up at launch — see message to the boss re: unsubstantiated claims.
  const testimonials = [
    ["plaza_test_1_body", "plaza_test_1_name", "priya"],
    ["plaza_test_2_body", "plaza_test_2_name", "marcus"],
    ["plaza_test_3_body", "plaza_test_3_name", "vendor-honey"],
  ];
  return (
    <section className="border-t border-stone-900 bg-stone-950/60 px-6 py-20 md:py-24">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl">
          <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
            {t("plaza_test_kicker")}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
            {t("plaza_test_title")}
          </h2>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-4"
              data-testid="plaza-sales-testimonials">
          {testimonials.map(([bodyKey, nameKey, seed]) => (
            <div key={nameKey}
                  className="rounded-2xl border border-stone-800 bg-stone-950/40 p-6 flex flex-col">
              <div className="flex text-amber-400 gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} fill="currentColor"/>
                ))}
              </div>
              <p className="mt-4 text-sm text-stone-200 leading-relaxed flex-1">
                &ldquo;{t(bodyKey)}&rdquo;
              </p>
              <div className="mt-6 flex items-center gap-3 pt-4 border-t border-stone-800">
                <img
                  src={`https://i.pravatar.cc/80?u=hustleplaza-${seed}`}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="text-sm font-bold text-stone-100">
                  {t(nameKey)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════ 13. FAQ ═════════════════════════ */
function FAQSection({ t }) {
  const qs = [
    ["plaza_faq_1_q", "plaza_faq_1_a"],
    ["plaza_faq_2_q", "plaza_faq_2_a"],
    ["plaza_faq_3_q", "plaza_faq_3_a"],
    ["plaza_faq_4_q", "plaza_faq_4_a"],
    ["plaza_faq_5_q", "plaza_faq_5_a"],
    ["plaza_faq_6_q", "plaza_faq_6_a"],
    ["plaza_faq_7_q", "plaza_faq_7_a"],
    ["plaza_faq_8_q", "plaza_faq_8_a"],
  ];
  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-20 md:py-24">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
          {t("plaza_faq_kicker")}
        </p>
        <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight">
          {t("plaza_faq_title")}
        </h2>
      </div>
      <div className="mt-12 space-y-2" data-testid="plaza-sales-faq">
        {qs.map(([q, a], i) => <FaqRow key={q} qKey={q} aKey={a} t={t} idx={i} />)}
      </div>
    </section>
  );
}

function FaqRow({ qKey, aKey, t, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className={`rounded-2xl border border-stone-800 bg-stone-950/40 overflow-hidden transition
                     ${open ? "border-amber-400/40" : ""}`}
          data-testid={`plaza-sales-faq-${idx + 1}`}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
      >
        <span className="font-bold text-stone-100">{t(qKey)}</span>
        <ChevronDown
          size={18}
          className={`text-stone-400 transition-transform ${open ? "rotate-180 text-amber-400" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-stone-400 leading-relaxed">
          {t(aKey)}
        </div>
      )}
    </div>
  );
}

/* ═════════════════════════ 15. FINAL CTA ═════════════════════════ */
function FinalCta({ t }) {
  return (
    <section
      id="founder"
      className="relative border-t border-stone-900 px-6 py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-amber-400/10 blur-3xl"/>
      <div className="relative max-w-3xl mx-auto text-center">
        <p className="text-amber-400 text-xs font-bold uppercase tracking-[0.28em]">
          {t("plaza_final_kicker")}
        </p>
        <h2 className="mt-3 text-4xl md:text-6xl font-black leading-tight">
          {t("plaza_final_title")}
        </h2>
        <p className="mt-5 text-stone-300 leading-relaxed">
          {t("plaza_final_sub")}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-4 py-2 text-emerald-300 text-xs font-bold">
          <Sparkles size={12}/> {t("plaza_final_bonus")}
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={`${APP_URL}/register?founder=1`}
            data-testid="plaza-sales-final-cta"
            className="inline-flex items-center gap-2 h-13 px-8 py-4 rounded-full text-base font-black text-stone-950 bg-amber-400 hover:bg-amber-300 shadow-[0_0_60px_-15px_#fbbf24aa] transition"
          >
            {t("plaza_final_cta")} <ArrowRight size={18} />
          </a>
        </div>
        <p className="mt-4 text-[11px] text-stone-500">
          {t("plaza_final_disclosure")}
        </p>
      </div>
    </section>
  );
}

/* ═════════════════════════ 15. FOOTER ═════════════════════════ */
function Footer({ t }) {
  return (
    <footer
      data-testid="plaza-sales-footer"
      className="border-t border-stone-800 bg-black px-6 py-12"
    >
      <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-8 text-sm">
        <div>
          <img src={LOGO_DARK} alt="HustlePlaza" className="h-9 w-auto"/>
          <p className="mt-4 text-stone-500 text-xs leading-relaxed">
            {t("plaza_footer_tagline")}
          </p>
        </div>
        <div>
          <div className="text-stone-400 text-[11px] uppercase tracking-widest font-bold">Shop</div>
          <ul className="mt-3 space-y-2 text-stone-300">
            <li><a href={`${APP_URL}/plaza/shop`} className="hover:text-amber-400">{t("plaza_cta_shop")}</a></li>
            <li><a href={`${APP_URL}/register`} className="hover:text-amber-400">{t("plaza_cta_start")}</a></li>
            <li><a href={`${APP_URL}/login`} className="hover:text-amber-400">{t("plaza_cta_signin")}</a></li>
          </ul>
        </div>
        <div>
          <div className="text-stone-400 text-[11px] uppercase tracking-widest font-bold">Company</div>
          <ul className="mt-3 space-y-2 text-stone-300">
            <li><a href="https://hustlesuite.au" className="hover:text-amber-400">HustleSuite</a></li>
            <li><a href={`${APP_URL}/vendors`} className="hover:text-amber-400">{t("plaza_footer_vendors")}</a></li>
            <li><a href={`${APP_URL}/help`} className="hover:text-amber-400">{t("plaza_footer_help")}</a></li>
            <li><a href={`${APP_URL}/contact`} className="hover:text-amber-400">{t("plaza_footer_contact")}</a></li>
          </ul>
        </div>
        <div>
          <div className="text-stone-400 text-[11px] uppercase tracking-widest font-bold">Legal</div>
          <ul className="mt-3 space-y-2 text-stone-300">
            <li><a href={`${APP_URL}/terms`} className="hover:text-amber-400">{t("plaza_footer_terms")}</a></li>
            <li><a href={`${APP_URL}/privacy`} className="hover:text-amber-400">{t("plaza_footer_privacy")}</a></li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-stone-900 text-[11px] text-stone-600 flex flex-wrap items-center gap-x-3 gap-y-1 dark:text-stone-400">
        <span>© {new Date().getFullYear()} HustlePlaza · part of{" "}
          <a href="https://hustlesuite.au" className="text-amber-400">HustleSuite</a>
        </span>
        <span className="text-stone-800 dark:text-stone-100">·</span>
        <span>{t("plaza_footer_owned")}</span>
      </div>
    </footer>
  );
}
